import { useEffect, useMemo, useState } from "react";
import {
  Alert, Box, Chip, CircularProgress, Divider, FormControl, Grid,
  InputLabel, MenuItem, Paper, Select, ToggleButton, ToggleButtonGroup, Typography,
} from "@mui/material";
import BarChartIcon from "@mui/icons-material/BarChart";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { budgetService } from "@/api/budget.service";
import { CATEGORIES } from "@/api/categories";
import type { HouseholdRecurringSummary, RecurringSummaryUser, RevenueMonth, ExpenseMonth } from "@/api/types";

const MONTHS_FULL = ["Janvier","Février","Mars","Avril","Mai","Juin","Juillet","Août","Septembre","Octobre","Novembre","Décembre"];
const MONTHS_SHORT = ["Jan","Fév","Mar","Avr","Mai","Jun","Jul","Aoû","Sep","Oct","Nov","Déc"];
const fmt = (n: number) => n.toLocaleString("fr-FR", { style: "currency", currency: "EUR" });
const USER_COLORS = ["#4caf50", "#2196f3", "#ff9800", "#9c27b0", "#e91e63", "#00bcd4"];

const now = new Date();
const MONTH_OPTIONS = Array.from({ length: 24 }, (_, i) => {
  const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
  return { value: `${d.getFullYear()}-${d.getMonth() + 1}`, month: d.getMonth() + 1, year: d.getFullYear(), label: `${MONTHS_FULL[d.getMonth()]} ${d.getFullYear()}` };
});
const YEAR_OPTIONS = Array.from({ length: 5 }, (_, i) => now.getFullYear() - i);

function getCat(value: string) {
  return CATEGORIES.find(c => c.value === value) ?? { label: value, color: "#9e9e9e" };
}

function CategoryChip({ category }: { category: string }) {
  const cat = getCat(category);
  return <Chip label={cat.label} size="small" sx={{ bgcolor: cat.color + "22", color: cat.color, fontWeight: 600, fontSize: "0.7rem" }} />;
}

function StackedTooltip({ active, payload, label, labelFor, sortDescending }: { active?: boolean; payload?: any[]; label?: string; labelFor: (key: string) => string; sortDescending?: boolean }) {
  if (!active || !payload || payload.length === 0) return null;
  const total = payload.reduce((s: number, p: any) => s + (Number(p.value) || 0), 0);
  const fullLabel = payload[0]?.payload?.fullLabel ?? label;
  const rows = sortDescending ? [...payload].sort((a, b) => Number(b.value) - Number(a.value)) : payload;
  return (
    <Box sx={{ backgroundColor: "#1e1e1e", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 1, p: 1.5 }}>
      <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>{fullLabel}</Typography>
      {rows.map((p: any) => (
        <Typography key={p.dataKey} variant="caption" sx={{ display: "block", color: p.color }}>
          {labelFor(p.dataKey)} : {fmt(Number(p.value))}
        </Typography>
      ))}
      <Divider sx={{ my: 0.5, borderColor: "rgba(255,255,255,0.12)" }} />
      <Typography variant="body2" fontWeight={700}>Total : {fmt(total)}</Typography>
    </Box>
  );
}

function RevenueExpenseTooltip({ active, payload, label }: { active?: boolean; payload?: any[]; label?: string }) {
  if (!active || !payload || payload.length === 0) return null;
  const fullLabel = payload[0]?.payload?.fullLabel ?? label;
  const revenueVal = Number(payload.find((p: any) => p.dataKey === "revenue")?.value ?? 0);
  const expenseVal = Number(payload.find((p: any) => p.dataKey === "expenses")?.value ?? 0);
  const balance = revenueVal - expenseVal;
  return (
    <Box sx={{ backgroundColor: "#1e1e1e", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 1, p: 1.5 }}>
      <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>{fullLabel}</Typography>
      <Typography variant="caption" sx={{ display: "block", color: "#4caf50" }}>Revenus : {fmt(revenueVal)}</Typography>
      <Typography variant="caption" sx={{ display: "block", color: "#f44336" }}>Dépenses : {fmt(expenseVal)}</Typography>
      <Divider sx={{ my: 0.5, borderColor: "rgba(255,255,255,0.12)" }} />
      <Typography variant="body2" fontWeight={700} color={balance >= 0 ? "success.main" : "error.main"}>Solde : {fmt(balance)}</Typography>
    </Box>
  );
}

function SingleValueTooltip({ active, payload, label, name, color }: { active?: boolean; payload?: any[]; label?: string; name: string; color: string }) {
  if (!active || !payload || payload.length === 0) return null;
  const fullLabel = payload[0]?.payload?.fullLabel ?? label;
  const value = Number(payload[0]?.value ?? 0);
  return (
    <Box sx={{ backgroundColor: "#1e1e1e", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 1, p: 1.5 }}>
      <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>{fullLabel}</Typography>
      <Typography variant="body2" fontWeight={700} sx={{ color }}>{name} : {fmt(value)}</Typography>
    </Box>
  );
}

function StatCard({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, flex: 1, minWidth: 160 }}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="h6" fontWeight={700}>{value}</Typography>
      {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
    </Paper>
  );
}

function UserRecurringCard({ user, annualize }: { user: RecurringSummaryUser; annualize: boolean }) {
  return (
    <Grid item xs={12} sm={6} key={user.userId}>
      <Paper elevation={1} sx={{ p: 3, borderRadius: 3 }}>
        <Typography fontWeight={700} sx={{ mb: 1 }}>{user.name}</Typography>
        <Typography variant="h6" fontWeight={700}>{fmt(user.total)}<Typography component="span" variant="body2" color="text.secondary"> / mois</Typography></Typography>
        {annualize && <Typography variant="caption" color="text.secondary">Soit {fmt(user.total * 12)} sur 12 mois</Typography>}
        <Box sx={{ display: "flex", gap: 2, mt: 1, mb: 2 }}>
          <Typography variant="caption" color="text.secondary">Partagé : {fmt(user.shared)}</Typography>
          <Typography variant="caption" color="text.secondary">Perso : {fmt(user.personal)}</Typography>
        </Box>
        <Divider sx={{ mb: 1.5 }} />
        {user.expenses.length === 0 ? (
          <Typography variant="body2" color="text.secondary">Aucune dépense récurrente.</Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {user.expenses.map(e => (
              <Box key={e.id} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                  <Typography variant="body2" noWrap>{e.label}</Typography>
                  <CategoryChip category={e.category} />
                  {e.sharedWith && <Chip label="Partagé" size="small" color="primary" variant="outlined" />}
                </Box>
                <Typography variant="body2" fontWeight={600}>{fmt(e.amount)}</Typography>
              </Box>
            ))}
          </Box>
        )}
      </Paper>
    </Grid>
  );
}

export default function StatisticsPage() {
  const [summary, setSummary] = useState<HouseholdRecurringSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [revenue, setRevenue] = useState<RevenueMonth[]>([]);
  const [revenueLoading, setRevenueLoading] = useState(true);
  const [revenueError, setRevenueError] = useState("");

  const [expenses, setExpenses] = useState<ExpenseMonth[]>([]);
  const [expenseLoading, setExpenseLoading] = useState(true);
  const [expenseError, setExpenseError] = useState("");

  const [selectedUserId, setSelectedUserId] = useState<string>("all");
  const [expenseView, setExpenseView] = useState<"user" | "category">("user");
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [timeframe, setTimeframe] = useState<1 | 12>(1);
  const [selectedMonthKey, setSelectedMonthKey] = useState(MONTH_OPTIONS[0].value);
  const [selectedYear, setSelectedYear] = useState(YEAR_OPTIONS[0]);

  const selectedMonth = MONTH_OPTIONS.find(o => o.value === selectedMonthKey)!;

  useEffect(() => {
    budgetService.getRecurringSummary()
      .then(setSummary)
      .catch(e => setError(e.response?.data?.error || "Erreur."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    setRevenueLoading(true);
    setRevenueError("");
    const params = timeframe === 12
      ? { resolution: 12 as const, year: selectedYear }
      : { resolution: 1 as const, year: selectedMonth.year, month: selectedMonth.month };
    budgetService.getRevenue(params)
      .then(setRevenue)
      .catch(e => setRevenueError(e.response?.data?.error || "Erreur."))
      .finally(() => setRevenueLoading(false));
  }, [timeframe, selectedMonthKey, selectedYear]);

  useEffect(() => {
    setExpenseLoading(true);
    setExpenseError("");
    const params = timeframe === 12
      ? { resolution: 12 as const, year: selectedYear }
      : { resolution: 1 as const, year: selectedMonth.year, month: selectedMonth.month };
    budgetService.getExpenses(params)
      .then(setExpenses)
      .catch(e => setExpenseError(e.response?.data?.error || "Erreur."))
      .finally(() => setExpenseLoading(false));
  }, [timeframe, selectedMonthKey, selectedYear]);

  const visibleUsers = summary ? (selectedUserId === "all" ? summary.byUser : summary.byUser.filter(u => u.userId === selectedUserId)) : [];
  const totals = selectedUserId === "all"
    ? summary?.household
    : visibleUsers[0] && { total: visibleUsers[0].total, shared: visibleUsers[0].shared, personal: visibleUsers[0].personal };

  const userColor = useMemo(() => {
    const ids = summary?.byUser.map(u => u.userId) ?? revenue[0]?.byUser.map(u => u.userId) ?? expenses[0]?.byUser.map(u => u.userId) ?? [];
    return new Map(ids.map((id, i) => [id, USER_COLORS[i % USER_COLORS.length]]));
  }, [summary, revenue, expenses]);

  const revenueUsers = revenue.length > 0
    ? (selectedUserId === "all" ? revenue[0].byUser : revenue[0].byUser.filter(u => u.userId === selectedUserId))
    : [];

  const revenueChartData = revenue.map(m => {
    const entry: Record<string, any> = {
      label: timeframe === 12 ? MONTHS_SHORT[m.month - 1] : `${MONTHS_FULL[m.month - 1]} ${m.year}`,
      fullLabel: `${MONTHS_FULL[m.month - 1]} ${m.year}`,
    };
    for (const u of revenueUsers) entry[u.userId] = m.byUser.find(x => x.userId === u.userId)?.income ?? 0;
    return entry;
  });

  const hasRevenueData = revenueChartData.some(row => revenueUsers.some(u => row[u.userId] > 0));

  const includedUserIds = expenses.length > 0
    ? (selectedUserId === "all" ? expenses[0].byUser.map(u => u.userId) : [selectedUserId])
    : [];

  const expenseUserSeries = expenses.length > 0
    ? (selectedUserId === "all" ? expenses[0].byUser : expenses[0].byUser.filter(u => u.userId === selectedUserId))
        .map(u => ({ key: u.userId, label: u.name, color: userColor.get(u.userId) ?? "#9e9e9e" }))
    : [];

  const expenseCategorySeries = (() => {
    const cats = new Set<string>();
    for (const m of expenses) {
      for (const u of m.byUser) {
        if (!includedUserIds.includes(u.userId)) continue;
        for (const c of u.categories) if (c.amount > 0) cats.add(c.category);
      }
    }
    return Array.from(cats).map(cat => {
      const c = getCat(cat);
      return { key: cat, label: c.label, color: c.color };
    });
  })();

  const expenseSeries = expenseView === "user" ? expenseUserSeries : expenseCategorySeries;

  const expenseChartData = expenses.map(m => {
    const entry: Record<string, any> = {
      label: timeframe === 12 ? MONTHS_SHORT[m.month - 1] : `${MONTHS_FULL[m.month - 1]} ${m.year}`,
      fullLabel: `${MONTHS_FULL[m.month - 1]} ${m.year}`,
    };
    for (const s of expenseSeries) {
      entry[s.key] = expenseView === "user"
        ? m.byUser.find(u => u.userId === s.key)?.expenses ?? 0
        : m.byUser
            .filter(u => includedUserIds.includes(u.userId))
            .reduce((sum, u) => sum + (u.categories.find(c => c.category === s.key)?.amount ?? 0), 0);
    }
    return entry;
  });

  const hasExpenseData = expenseChartData.some(row => expenseSeries.some(s => row[s.key] > 0));

  const combinedChartData = revenue.map((rm, i) => {
    const em = expenses[i];
    const revenueTotal = selectedUserId === "all" ? rm.total : rm.byUser.find(u => u.userId === selectedUserId)?.income ?? 0;
    const expenseTotal = selectedUserId === "all" ? (em?.total ?? 0) : em?.byUser.find(u => u.userId === selectedUserId)?.expenses ?? 0;
    return {
      label: MONTHS_SHORT[rm.month - 1],
      fullLabel: `${MONTHS_FULL[rm.month - 1]} ${rm.year}`,
      revenue: revenueTotal,
      expenses: expenseTotal,
    };
  });

  const hasCombinedData = combinedChartData.some(row => row.revenue > 0 || row.expenses > 0);

  const categoryOptions = expenseCategorySeries;
  const effectiveCategory = categoryOptions.some(c => c.key === selectedCategory) ? selectedCategory : (categoryOptions[0]?.key ?? "");
  const effectiveCategoryMeta = getCat(effectiveCategory);

  const categoryChartData = expenses.map(m => ({
    label: MONTHS_SHORT[m.month - 1],
    fullLabel: `${MONTHS_FULL[m.month - 1]} ${m.year}`,
    amount: m.byUser
      .filter(u => includedUserIds.includes(u.userId))
      .reduce((sum, u) => sum + (u.categories.find(c => c.category === effectiveCategory)?.amount ?? 0), 0),
  }));

  const hasCategoryData = Boolean(effectiveCategory) && categoryChartData.some(row => row.amount > 0);

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 900, mx: "auto" }}>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 3 }}>Statistiques</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Filters */}
      <Paper elevation={1} sx={{ p: 3, borderRadius: 3, mb: 3 }}>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Membre</InputLabel>
            <Select value={selectedUserId} label="Membre" onChange={e => setSelectedUserId(e.target.value)}>
              <MenuItem value="all">Tous</MenuItem>
              {summary?.byUser.map(u => <MenuItem key={u.userId} value={u.userId}>{u.name}</MenuItem>)}
            </Select>
          </FormControl>
          <ToggleButtonGroup value={timeframe} exclusive onChange={(_, v) => v && setTimeframe(v)} size="small">
            <ToggleButton value={1}>1 mois</ToggleButton>
            <ToggleButton value={12}>12 mois</ToggleButton>
          </ToggleButtonGroup>
          {timeframe === 1 ? (
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Mois</InputLabel>
              <Select value={selectedMonthKey} label="Mois" onChange={e => setSelectedMonthKey(e.target.value)}>
                {MONTH_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </Select>
            </FormControl>
          ) : (
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Année</InputLabel>
              <Select value={selectedYear} label="Année" onChange={e => setSelectedYear(Number(e.target.value))}>
                {YEAR_OPTIONS.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
              </Select>
            </FormControl>
          )}
        </Box>
      </Paper>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress /></Box>
      ) : (
        <>
          {/* Section 1: recurring expenses summary */}
          <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>Dépenses récurrentes du foyer</Typography>
          {totals && (
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 3 }}>
              <StatCard label="Total mensuel" value={fmt(totals.total)} sub={timeframe === 12 ? `Soit ${fmt(totals.total * 12)} sur 12 mois` : undefined} />
              <StatCard label="Part partagée" value={fmt(totals.shared)} />
              <StatCard label="Part personnelle" value={fmt(totals.personal)} />
            </Box>
          )}
          <Grid container spacing={2} sx={{ mb: 4 }}>
            {visibleUsers.map(u => <UserRecurringCard key={u.userId} user={u} annualize={timeframe === 12} />)}
          </Grid>

          {/* Section 2: charts */}
          <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>Graphiques</Typography>

          <Paper elevation={1} sx={{ p: 3, borderRadius: 3, mb: 3 }}>
            <Typography fontWeight={600} sx={{ mb: 2 }}>Revenus vs Dépenses</Typography>
            {timeframe !== 12 ? (
              <Alert severity="info">Ce graphique est disponible uniquement pour la vue « 12 mois ».</Alert>
            ) : revenueError || expenseError ? (
              <Alert severity="error">{revenueError || expenseError}</Alert>
            ) : revenueLoading || expenseLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress /></Box>
            ) : !hasCombinedData ? (
              <Alert severity="info">Aucune donnée pour cette période.</Alert>
            ) : (
              <ResponsiveContainer width="100%" height={340}>
                <LineChart data={combinedChartData} margin={{ top: 4, right: 16, left: 8, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.07)" />
                  <XAxis dataKey="label" tick={{ fill: "rgba(255,255,255,0.6)", fontSize: 12 }} />
                  <YAxis tickFormatter={v => `${v}€`} tick={{ fill: "rgba(255,255,255,0.6)", fontSize: 11 }} width={56} />
                  <Tooltip content={<RevenueExpenseTooltip />} wrapperStyle={{ zIndex: 10 }} />
                  <Legend formatter={key => key === "revenue" ? "Revenus" : "Dépenses"} />
                  <Line type="monotone" dataKey="revenue" stroke="#4caf50" strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="expenses" stroke="#f44336" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </Paper>

          <Paper elevation={1} sx={{ p: 3, borderRadius: 3, mb: 3 }}>
            <Typography fontWeight={600} sx={{ mb: 2 }}>Revenus du foyer</Typography>
            {revenueError && <Alert severity="error" sx={{ mb: 2 }}>{revenueError}</Alert>}
            {revenueLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress /></Box>
            ) : !hasRevenueData ? (
              <Alert severity="info">Aucun revenu pour cette période.</Alert>
            ) : (
              <ResponsiveContainer width="100%" height={340}>
                <BarChart data={revenueChartData} margin={{ top: 4, right: 16, left: 8, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.07)" />
                  <XAxis dataKey="label" tick={{ fill: "rgba(255,255,255,0.6)", fontSize: 12 }} />
                  <YAxis tickFormatter={v => `${v}€`} tick={{ fill: "rgba(255,255,255,0.6)", fontSize: 11 }} width={56} />
                  <Tooltip content={(props: any) => <StackedTooltip {...props} labelFor={key => revenueUsers.find(u => u.userId === key)?.name ?? key} />} wrapperStyle={{ zIndex: 10 }} />
                  <Legend formatter={key => revenueUsers.find(u => u.userId === key)?.name ?? key} />
                  {revenueUsers.map(u => (
                    <Bar key={u.userId} dataKey={u.userId} stackId="revenue" fill={userColor.get(u.userId) ?? "#9e9e9e"} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            )}
          </Paper>

          <Paper elevation={1} sx={{ p: 3, borderRadius: 3, mb: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1, mb: 2 }}>
              <Typography fontWeight={600}>Dépenses du foyer</Typography>
              <ToggleButtonGroup value={expenseView} exclusive onChange={(_, v) => v && setExpenseView(v)} size="small">
                <ToggleButton value="user">Par membre</ToggleButton>
                <ToggleButton value="category">Par catégorie</ToggleButton>
              </ToggleButtonGroup>
            </Box>
            {expenseError && <Alert severity="error" sx={{ mb: 2 }}>{expenseError}</Alert>}
            {expenseLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress /></Box>
            ) : !hasExpenseData ? (
              <Alert severity="info">Aucune dépense pour cette période.</Alert>
            ) : (
              <ResponsiveContainer width="100%" height={340}>
                <BarChart data={expenseChartData} margin={{ top: 4, right: 16, left: 8, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.07)" />
                  <XAxis dataKey="label" tick={{ fill: "rgba(255,255,255,0.6)", fontSize: 12 }} />
                  <YAxis tickFormatter={v => `${v}€`} tick={{ fill: "rgba(255,255,255,0.6)", fontSize: 11 }} width={56} />
                  <Tooltip content={(props: any) => <StackedTooltip {...props} labelFor={key => expenseSeries.find(s => s.key === key)?.label ?? key} sortDescending={expenseView === "category"} />} wrapperStyle={{ zIndex: 10 }} />
                  <Legend formatter={key => expenseSeries.find(s => s.key === key)?.label ?? key} />
                  {expenseSeries.map(s => (
                    <Bar key={s.key} dataKey={s.key} stackId="expenses" fill={s.color} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            )}
          </Paper>

          <Paper elevation={1} sx={{ p: 3, borderRadius: 3, mb: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1, mb: 2 }}>
              <Typography fontWeight={600}>Évolution d'une catégorie</Typography>
              {timeframe === 12 && categoryOptions.length > 0 && (
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>Catégorie</InputLabel>
                  <Select value={effectiveCategory} label="Catégorie" onChange={e => setSelectedCategory(e.target.value)}>
                    {categoryOptions.map(c => (
                      <MenuItem key={c.key} value={c.key}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: c.color, flexShrink: 0 }} />
                          {c.label}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>
            {timeframe !== 12 ? (
              <Alert severity="info">Ce graphique est disponible uniquement pour la vue « 12 mois ».</Alert>
            ) : expenseError ? (
              <Alert severity="error">{expenseError}</Alert>
            ) : expenseLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress /></Box>
            ) : !hasCategoryData ? (
              <Alert severity="info">Aucune dépense pour cette période.</Alert>
            ) : (
              <ResponsiveContainer width="100%" height={340}>
                <LineChart data={categoryChartData} margin={{ top: 4, right: 16, left: 8, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.07)" />
                  <XAxis dataKey="label" tick={{ fill: "rgba(255,255,255,0.6)", fontSize: 12 }} />
                  <YAxis tickFormatter={v => `${v}€`} tick={{ fill: "rgba(255,255,255,0.6)", fontSize: 11 }} width={56} />
                  <Tooltip content={<SingleValueTooltip name={effectiveCategoryMeta.label} color={effectiveCategoryMeta.color} />} wrapperStyle={{ zIndex: 10 }} />
                  <Line type="monotone" dataKey="amount" name={effectiveCategoryMeta.label} stroke={effectiveCategoryMeta.color} strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </Paper>

          <Paper elevation={1} sx={{ p: 3, borderRadius: 3 }}>
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1, py: 6, color: "text.secondary" }}>
              <BarChartIcon sx={{ fontSize: 40 }} />
              <Typography>D'autres graphiques arrivent bientôt.</Typography>
            </Box>
          </Paper>
        </>
      )}
    </Box>
  );
}
